import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import i18n from "config/i18n"
import { Grid, Typography, styled } from "@mui/material"

import { useAppDispatch, useAppSelector } from "redux/hooks"
import { selectFetchStatus } from "redux/features/client/clientSlice"
import {
  makePhoneConfirmation,
  makeResendPhoneConfirmation,
  makeCancelPhoneConfirmation,
} from "redux/features/client/phoneConfirmationThunk"
import { showAlert, showNotice } from "redux/features/notice/noticeSlice"
import Button from "components/ui/Button"
import Input from "components/ui/Input"
import { ClientConfirmationFormValues } from "types"

type FormProps = {
  confirmation: ClientConfirmationFormValues
  onConfirmation: () => void
}

const PhoneConfirmationForm = ({ confirmation, onConfirmation }: FormProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const fetchStatus = useAppSelector((state) => selectFetchStatus(state))
  const [isSubmitting, setSubmitting] = useState(false)
  const [verificationCode, setVerificationCode] = useState("")
  const [errorMesssage, setErrorMesssage] = useState("")
  const { unconfirmed_phone } = confirmation
  const isUnconfirmedPhone = unconfirmed_phone && unconfirmed_phone.length > 0

  const handleSubmit = () => {
    if (!fetchStatus.isFetching) {
      if (verificationCode && verificationCode.length > 0) {
        setSubmitting(true)
      } else {
        setErrorMesssage(
          i18n.t("validation.field.required", {
            field: i18n.t("features.client.verification_code"),
          }),
        )
        return
      }
      dispatch(makePhoneConfirmation({ code: verificationCode }))
        .then((result) => {
          dispatch(showNotice({ message: result }))
          onConfirmation()
        })
        .catch((error) => {
          dispatch(showAlert({ message: error }))
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  }

  const handleResend = () => {
    if (!fetchStatus.isFetching) {
      dispatch(makeResendPhoneConfirmation())
        .then((result) => {
          dispatch(showNotice({ message: result }))
        })
        .catch((error) => {
          dispatch(showAlert({ message: error }))
        })
    }
  }

  const handleCancel = () => {
    if (!fetchStatus.isFetching) {
      dispatch(makeCancelPhoneConfirmation())
        .then((result) => {
          dispatch(showNotice({ message: result }))
          onConfirmation()
        })
        .catch((error) => {
          dispatch(showAlert({ message: error }))
        })
    }
  }

  return (
    <>
      {isUnconfirmedPhone && (
        <Wrapper
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Item>
            <Typography>
              {t("features.client.confirmation.phone.title", {
                unconfirmed_phone,
              })}
            </Typography>
          </Item>
          <Item>
            <Input
              fullWidth
              label={t("features.client.verification_code")}
              helperText={errorMesssage}
              disabled={isSubmitting}
              onChange={(e) => {
                setVerificationCode(e.target.value)
              }}
            />
            <Submit onClick={handleSubmit}>
              {t("features.client.confirmation.phone.submit")}
            </Submit>
            <Resend onClick={handleResend}>
              {t("features.client.confirmation.phone.resend")}
            </Resend>
            <Cancel onClick={handleCancel}>
              {t("features.client.confirmation.phone.cancel")}
            </Cancel>
          </Item>
        </Wrapper>
      )}
    </>
  )
}

export default PhoneConfirmationForm

const Wrapper = styled(Grid)`
  padding: 4px 4px 0 4px;
`
const Item = styled(Grid)`
  display: flex;
  flex-direction: row;
  margin: 0;
`
const Submit = styled(Button)`
  background: rgba(0, 169, 149, 0.07);
  color: ${({ theme }) => theme.palette.primary.main};
  border-radius: 21px;
  font-weight: 600;
  height: 56px;
  max-width: 175px;
  padding: 0 12px;
  margin: 0px 4px;
  width: auto;
`
const Resend = styled(Button)`
  background: rgba(0, 169, 149, 0.07);
  color: ${({ theme }) => theme.palette.primary.main};
  border-radius: 21px;
  font-weight: 600;
  height: 56px;
  max-width: 175px;
  padding: 0 12px;
  margin: 0px 4px;
  width: auto;
`
const Cancel = styled(Button)`
  background: rgba(172, 172, 172, 0.1);
  color: ${({ theme }) => theme.palette.common.black};
  border-radius: 21px;
  font-weight: 600;
  height: 56px;
  max-width: 175px;
  padding: 0 12px;
  margin: 0px 4px;
  width: auto;
`
